import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickStartGuideExampleImage from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExampleImage";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import { START_GUIDE_IMAGES } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const KEY = "path_pick_guide:smart_store_upload_guide.";
// 2)·3) 문구는 API 흐름의 등록 예시 모달(PathPickStartGuideRegistrationExampleModal)과
// 같은 내용이다 — 한 문구를 두 벌로 복제하지 않고 그쪽 키를 그대로 참조한다.
const EXAMPLE_KEY = "path_pick_guide:registration_example.";

/**
 * 스마트스토어 업로드와 등록 예시 안내.
 *
 * 등록 신청 내역에서 엑셀 파일을 받은 뒤 `등록 예시 보기` 로 연다. 파일을 어디에 올리는지부터
 * 올린 뒤 관리자에서 무엇을 더 해야 하는지(전시 상태 변경), 스토어에 어떻게 보이는지까지
 * 실제로 밟는 순서대로 1) 2) 3) 으로 번호를 매겨 한 모달에 모았다.
 * 가이드에서는 실제 파일이 만들어지지 않으므로 그 사실은 맨 아래 안내문으로만 남긴다.
 */
function PathPickStartGuideSmartStoreUploadGuideModal({
  isOpen,
  onClose,
}: Props) {
  const { t } = useI18nHelper();

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"min(960px, 92vw)"}
      maxWidth={"92vw"}
      maxHeight={"92vh"}
      scrollBehavior={"inside"}
      isCentered={true}
    >
      <CustomApprovalModal.Header onClose={onClose} title={t(`${KEY}title`)} />
      <CustomApprovalModal.Body overflow={"auto"}>
        <Flex flexDirection={"column"} gap={"20px"}>
          <Flex flexDirection={"column"} gap={"10px"}>
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${KEY}step_1_title`)}
            </Text>
            <PathPickStartGuideMenuPath path={t(`${KEY}menu_path`)} />
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              whiteSpace={"pre-line"}
            >
              {t(`${KEY}description`)}
            </Text>
            <Flex flexDirection={"column"} gap={"6px"}>
              <PathPickStartGuideExampleImage
                src={START_GUIDE_IMAGES.smartStoreProductListing}
                alt={t(`${KEY}image_alt`)}
                placeholderHeight={"320px"}
              />
              <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                {t(`${KEY}image_caption`)}
              </Text>
            </Flex>
          </Flex>

          <Flex flexDirection={"column"} gap={"10px"}>
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${KEY}step_2_title`)}
            </Text>
            <PathPickStartGuideMenuPath path={t(`${KEY}step_2_menu_path`)} />
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {t(`${EXAMPLE_KEY}section_1`)}
            </Text>
            {/* 관리자 화면은 좌우로 읽어야 해서 모달 전체 너비에 가깝게 둔다. */}
            <PathPickStartGuideExampleImage
              src={START_GUIDE_IMAGES.smartStoreRegistrationAdmin}
              alt={t(`${EXAMPLE_KEY}section_1_image_alt`)}
              placeholderHeight={"320px"}
            />
          </Flex>

          <Flex flexDirection={"column"} gap={"10px"}>
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${KEY}step_3_title`)}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {t(`${EXAMPLE_KEY}section_2`)}
            </Text>
            {/* 스토어 노출 예시는 모바일 화면이라 가운데 정렬하고 420px로 제한한다(전달서 §11). */}
            <Flex justifyContent={"center"}>
              <PathPickStartGuideExampleImage
                src={START_GUIDE_IMAGES.smartStoreRegistrationProduct}
                alt={t(`${EXAMPLE_KEY}section_2_image_alt`)}
                maxWidth={"420px"}
                placeholderHeight={"420px"}
              />
            </Flex>
          </Flex>

          <PathPickInlineNotice tone={"gray"} leadingIcon={"info-circle"}>
            {t(`${KEY}notice`)}
          </PathPickInlineNotice>

          <Flex justifyContent={"flex-end"}>
            <AppButton variant={"primary"} onClick={onClose}>
              {t("path_pick_guide:close")}
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickStartGuideSmartStoreUploadGuideModal;
